import type { TopLevelSpec } from "vega-lite";
import type { Channel } from "vega-lite/types_unstable/channel.js";
import type { Field } from "vega-lite/types_unstable/channeldef.js";
import type { Encoding } from "vega-lite/types_unstable/encoding.js";
import type { Scale } from "vega-lite/types_unstable/scale.js";

import type { MetricsQueryRow } from "src/analytics/metrics/useMetricsQuery";
import {
  SPECIAL_DOMAINS,
  type SpecialDomain,
} from "src/analytics/metrics/VisualizeMetrics/liveAnalyticsDomains";
import { coerceToString, hashString } from "src/utils/stringUtils";

// Vega-Lite v6 container shapes the tree walk recurses into.
const CHILD_SPEC_KEYS = ["layer", "vconcat", "hconcat", "concat"] as const;
// Facet/repeat place their inner view at the top-level "spec" key in v6.
const NESTED_SPEC_KEYS = ["spec"] as const;

const USERMETA_KEY = "usermeta";
const TAKTILE_NS = "taktile";
const SPECIAL_DOMAINS_FIELD = "specialDomains";

// Derived column appended via `transform: calculate` so tooltips referencing
// a bound UUID field can be retargeted at a human-readable column without
// changing the encoded field (which keeps the color scale's domain keyed on
// UUIDs and stable across charts). `__tktl_` is the reserved taktile prefix
// (see src/analytics/utils.ts).
const LABEL_FIELD_PREFIX = "__tktl_label_";

type SpecialDomainKey = keyof typeof SPECIAL_DOMAINS;

type OrdinalScale = {
  readonly domain: readonly string[];
  readonly range: readonly string[];
};

type SpecialDomainBinding = {
  channel: Channel;
  field: string;
  domain: SpecialDomainKey;
};

export type LabelDictionaries = Partial<
  Record<SpecialDomainKey, Record<string, string>>
>;

// The LLM-emitted JSON only matches `TopLevelSpec` at the root; child nodes
// after recursion are arbitrary sub-specs from a deeply unioned hierarchy.
// We walk them structurally and only narrow at the leaf operations
// (`encoding`, `scale`).
type SpecNode = Record<string, unknown>;

const isRecord = (v: unknown): v is SpecNode =>
  v !== null && typeof v === "object" && !Array.isArray(v);

const isSpecialDomain = (v: unknown): v is SpecialDomainKey =>
  typeof v === "string" && v in SPECIAL_DOMAINS;

const extractBindings = (spec: SpecNode): SpecialDomainBinding[] => {
  const usermeta = spec[USERMETA_KEY];
  if (!isRecord(usermeta)) return [];
  const namespace = usermeta[TAKTILE_NS];
  if (!isRecord(namespace)) return [];
  const raw = namespace[SPECIAL_DOMAINS_FIELD];
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((entry): SpecialDomainBinding[] => {
    if (!isRecord(entry)) return [];
    const { channel, field, domain } = entry;
    if (typeof channel !== "string") return [];
    if (typeof field !== "string") return [];
    if (!isSpecialDomain(domain)) return [];
    return [{ channel: channel as Channel, field, domain }];
  });
};

const collectDomain = (rows: MetricsQueryRow[], field: string): string[] => {
  const set = rows.reduce<Set<string>>((acc, row) => {
    if (!(field in row)) return acc;
    acc.add(coerceToString(row[field]));
    return acc;
  }, new Set<string>());
  return Array.from(set).sort();
};

const resolveBoundedScale = (visual: SpecialDomain): OrdinalScale | null => {
  if (!visual.values || visual.palette.length === 0) return null;
  return {
    domain: visual.values,
    range: visual.palette.slice(0, visual.values.length),
  };
};

const resolveUnboundedScale = (
  visual: SpecialDomain,
  rows: MetricsQueryRow[],
  field: string,
): OrdinalScale | null => {
  if (visual.palette.length === 0) return null;
  const domain = collectDomain(rows, field);
  if (domain.length === 0) return null;
  return {
    domain,
    range: domain.map(
      (v) => visual.palette[hashString(v) % visual.palette.length],
    ),
  };
};

const resolveScale = (
  visual: SpecialDomain,
  rows: MetricsQueryRow[],
  field: string,
): OrdinalScale | null =>
  visual.values
    ? resolveBoundedScale(visual)
    : resolveUnboundedScale(visual, rows, field);

// Restrict the caller-provided UUID→name dictionary to ids that appear in the
// rows. Keeps the Vega expression we inline small even when a workspace has
// thousands of versions but the chart only touches a handful.
const resolveLabelMap = (
  rows: MetricsQueryRow[],
  field: string,
  fullDict: Record<string, string> | undefined,
): Record<string, string> | null => {
  if (!fullDict) return null;
  const present = collectDomain(rows, field);
  const entries = present.flatMap((id): Array<[string, string]> => {
    const name = fullDict[id];
    return typeof name === "string" ? [[id, name]] : [];
  });
  if (entries.length === 0) return null;
  return Object.fromEntries(entries);
};

const buildLookupExpr = (
  dict: Record<string, string>,
  keyExpr: string,
): string => `(${JSON.stringify(dict)})[${keyExpr}] || ${keyExpr}`;

const labelFieldName = (field: string): string =>
  `${LABEL_FIELD_PREFIX}${field}`;

const applyLabelExprToAxisOrLegend = (
  channelDef: SpecNode,
  property: "axis" | "legend",
  labelExpr: string,
): SpecNode => {
  const existing = channelDef[property];
  // `legend: null` / `axis: null` means the LLM is telling Vega to hide it —
  // don't resurrect a hidden axis/legend just to attach a labelExpr.
  if (existing === null) return channelDef;
  const base = isRecord(existing) ? existing : {};
  return { ...channelDef, [property]: { ...base, labelExpr } };
};

// Channels that take the palette scale. Writing the palette into a
// positional (x/y) scale.range would replace Vega's pixel range with
// color strings.
const PALETTE_CHANNELS: readonly string[] = ["color", "fill", "stroke"];

const applyPaletteScale = (
  channelDef: SpecNode,
  visual: SpecialDomain,
  rows: MetricsQueryRow[],
  field: string,
): SpecNode => {
  const computed = resolveScale(visual, rows, field);
  if (!computed) return channelDef;

  const existingScale: Scale = isRecord(channelDef.scale)
    ? (channelDef.scale as Scale)
    : {};

  // If the LLM pre-set EITHER domain or range, keep both as-is — pairing
  // a half-set scale with our full computed counterpart produces a Vega
  // ordinal scale with mismatched lengths, which renders incorrectly.
  const llmPresetEither =
    existingScale.domain !== undefined || existingScale.range !== undefined;
  const scale: Scale = llmPresetEither
    ? existingScale
    : {
        ...existingScale,
        domain: [...computed.domain],
        range: [...computed.range],
      };

  return { ...channelDef, scale };
};

const applyBindingToNode = (
  node: SpecNode,
  binding: SpecialDomainBinding,
  rows: MetricsQueryRow[],
  labelDictionaries: LabelDictionaries,
): SpecNode => {
  if (!isRecord(node.encoding)) return node;
  // Encoding<Field> is the documented shape we expect; channel lookups return
  // a wide union including value-only defs that lack `field`/`scale`, so we
  // drop back to a structural record after asserting the encoding shape.
  const encoding: Encoding<Field> = node.encoding as Encoding<Field>;
  const channelDef: unknown = (encoding as Record<string, unknown>)[
    binding.channel
  ];
  if (!isRecord(channelDef)) return node;
  if (channelDef.field !== binding.field) return node;

  const visual = SPECIAL_DOMAINS[binding.domain];
  const withScale = PALETTE_CHANNELS.includes(binding.channel)
    ? applyPaletteScale(channelDef, visual, rows, binding.field)
    : channelDef;

  const labelMap = resolveLabelMap(
    rows,
    binding.field,
    labelDictionaries[binding.domain],
  );
  // Tick labels and legend entries get the labelExpr remap; the field value
  // (which the color scale's domain is keyed on) stays the UUID so colors
  // remain stable across charts.
  const labelExpr =
    labelMap === null ? null : buildLookupExpr(labelMap, "datum.value");
  const withLabels =
    labelExpr === null
      ? withScale
      : applyLabelExprToAxisOrLegend(
          applyLabelExprToAxisOrLegend(withScale, "axis", labelExpr),
          "legend",
          labelExpr,
        );

  return {
    ...node,
    encoding: {
      ...encoding,
      [binding.channel]: withLabels,
    },
  };
};

// `encoding.tooltip` is FieldDef | FieldDef[] | null | true — for the
// explicit-field shapes we retarget refs to the bound field at the derived
// label column. `tooltip: true` / `mark.tooltip: true` fall back to UUIDs
// because materializing an explicit channel array would change semantics
// (e.g. which fields appear) — out of scope here.
const retargetTooltipFieldDef = (
  fieldDef: unknown,
  fieldsToRetarget: Set<string>,
): unknown => {
  if (!isRecord(fieldDef)) return fieldDef;
  const field = fieldDef.field;
  if (typeof field !== "string" || !fieldsToRetarget.has(field))
    return fieldDef;
  // Preserve a caller-set title so a friendly label survives; default to the
  // original field name so the tooltip header doesn't read `__tktl_label_…`.
  const title = fieldDef.title ?? field;
  return { ...fieldDef, field: labelFieldName(field), title };
};

const retargetTooltipEncoding = (
  node: SpecNode,
  fieldsToRetarget: Set<string>,
): SpecNode => {
  if (fieldsToRetarget.size === 0) return node;
  if (!isRecord(node.encoding)) return node;
  const encoding = node.encoding as SpecNode;
  const tooltip = encoding.tooltip;
  if (tooltip === undefined || tooltip === null) return node;
  if (typeof tooltip === "boolean") return node;

  const retargeted = Array.isArray(tooltip)
    ? tooltip.map((entry) => retargetTooltipFieldDef(entry, fieldsToRetarget))
    : retargetTooltipFieldDef(tooltip, fieldsToRetarget);
  if (retargeted === tooltip) return node;
  return { ...node, encoding: { ...encoding, tooltip: retargeted } };
};

const applyToTree = (
  node: SpecNode,
  bindings: SpecialDomainBinding[],
  rows: MetricsQueryRow[],
  labelDictionaries: LabelDictionaries,
  retargetedFields: Set<string>,
): SpecNode => {
  const here = bindings.reduce(
    (acc, binding) => applyBindingToNode(acc, binding, rows, labelDictionaries),
    node,
  );

  const withTooltips = retargetTooltipEncoding(here, retargetedFields);

  const withListChildren = CHILD_SPEC_KEYS.reduce<SpecNode>((acc, key) => {
    const children = acc[key];
    if (!Array.isArray(children)) return acc;
    return {
      ...acc,
      [key]: children.map((child) =>
        isRecord(child)
          ? applyToTree(
              child,
              bindings,
              rows,
              labelDictionaries,
              retargetedFields,
            )
          : child,
      ),
    };
  }, withTooltips);

  return NESTED_SPEC_KEYS.reduce<SpecNode>((acc, key) => {
    const child = acc[key];
    if (!isRecord(child)) return acc;
    return {
      ...acc,
      [key]: applyToTree(
        child,
        bindings,
        rows,
        labelDictionaries,
        retargetedFields,
      ),
    };
  }, withListChildren);
};

// Multiple bindings can reference the same field (e.g. color + x both bound
// to flow_version_id) — emit only one transform per field.
const buildLabelTransforms = (
  bindings: SpecialDomainBinding[],
  rows: MetricsQueryRow[],
  labelDictionaries: LabelDictionaries,
): { transforms: SpecNode[]; retargetedFields: Set<string> } => {
  const byField = bindings.reduce<Map<string, Record<string, string>>>(
    (acc, binding) => {
      if (acc.has(binding.field)) return acc;
      const map = resolveLabelMap(
        rows,
        binding.field,
        labelDictionaries[binding.domain],
      );
      if (map === null) return acc;
      acc.set(binding.field, map);
      return acc;
    },
    new Map(),
  );

  const transforms = Array.from(byField.entries()).map(
    ([field, map]): SpecNode => ({
      calculate: buildLookupExpr(map, `datum[${JSON.stringify(field)}]`),
      as: labelFieldName(field),
    }),
  );
  return { transforms, retargetedFields: new Set(byField.keys()) };
};

const addRootTransforms = (
  spec: SpecNode,
  transforms: SpecNode[],
): SpecNode => {
  if (transforms.length === 0) return spec;
  const existing = Array.isArray(spec.transform) ? spec.transform : [];
  return { ...spec, transform: [...existing, ...transforms] };
};

const stripHere = (spec: SpecNode): SpecNode => {
  const usermeta = spec[USERMETA_KEY];
  if (!isRecord(usermeta)) return spec;
  const { [TAKTILE_NS]: removed, ...rest } = usermeta;
  if (removed === undefined) return spec;
  if (Object.keys(rest).length === 0) {
    const { [USERMETA_KEY]: dropped, ...specWithoutUsermeta } = spec;
    return dropped === undefined ? spec : specWithoutUsermeta;
  }
  return { ...spec, [USERMETA_KEY]: rest };
};

// The backend only writes `usermeta.taktile` at the root today, but recursing
// matches the documented contract ("never reaches vega-embed") and is robust
// to a future per-layer annotation or an LLM placing it on a nested view.
const stripTaktileUsermeta = (spec: SpecNode): SpecNode => {
  const here = stripHere(spec);

  const withListChildren = CHILD_SPEC_KEYS.reduce<SpecNode>((acc, key) => {
    const children = acc[key];
    if (!Array.isArray(children)) return acc;
    return {
      ...acc,
      [key]: children.map((child) =>
        isRecord(child) ? stripTaktileUsermeta(child) : child,
      ),
    };
  }, here);

  return NESTED_SPEC_KEYS.reduce<SpecNode>((acc, key) => {
    const child = acc[key];
    if (!isRecord(child)) return acc;
    return { ...acc, [key]: stripTaktileUsermeta(child) };
  }, withListChildren);
};

// Materializes scales from the local SPECIAL_DOMAINS and strips the backend's
// `usermeta.taktile` annotation on the way out so it never reaches
// vega-embed. Coloring lives entirely on this side — the backend only names
// which channels carry which special domain. Palette scales attach to
// color/fill/stroke bindings only; positional (x/y) bindings get the
// labelExpr/tooltip relabeling without a scale.
//
// When `labelDictionaries` carries UUID→name maps for identifier domains
// (flow_id, flow_version_id):
//   1. axis.labelExpr / legend.labelExpr remap tick + legend text to names
//      while the encoded field value stays the UUID (color stability).
//   2. A root `transform: calculate` adds a `${field}__label` column.
//   3. Tooltip channelDefs that reference a bound field are retargeted to
//      read from the derived `__label` column.
export const applySpecialDomainBindings = (
  spec: TopLevelSpec,
  rows: MetricsQueryRow[],
  labelDictionaries: LabelDictionaries = {},
): TopLevelSpec => {
  const root = spec as unknown as SpecNode;
  const bindings = extractBindings(root);
  const { transforms, retargetedFields } = buildLabelTransforms(
    bindings,
    rows,
    labelDictionaries,
  );
  const withTransforms = addRootTransforms(root, transforms);
  const transformed =
    bindings.length === 0
      ? withTransforms
      : applyToTree(
          withTransforms,
          bindings,
          rows,
          labelDictionaries,
          retargetedFields,
        );
  return stripTaktileUsermeta(transformed) as unknown as TopLevelSpec;
};
